<template>
  <div class="atc">
    <div class="atc-head drawer_line">
      <span class="atc-head__title">{{ $t('statInfo.trendChart') }}</span>
      <el-button
        v-show="!collapsed"
        type="text"
        class="atc-head__btn"
        :disabled="!canAdd"
        @click="$emit('add')"
      >+ {{ $t('abroadStat.addChart') }}</el-button>
      <el-button type="text" class="atc-head__btn" @click="$emit('update:collapsed', !collapsed)">
        {{ collapsed ? $t('statInfo.open') : $t('statInfo.close') }}
        <i :class="collapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
      </el-button>
    </div>
    <div v-show="!collapsed" class="atc-grid">
      <div
        v-for="(item, index) in charts"
        :key="item.id"
        class="atc-slot"
        :class="slotClass(index)"
      >
        <ChartCard
          ref="cards"
          :chart="item"
          :metricOptions="metricOptions"
          :dimensionOptions="dimensionOptions"
          :compareLabels="compareLabels"
          :axisWidth="sharedAxisWidth"
          :canRemove="charts.length > 1"
          @change="patch => $emit('change', item.id, patch)"
          @remove="$emit('remove', item.id)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ChartCard from './ChartCard.vue'
import { yAxisWidth } from '../../utils/chartAxis'

const MAX_CHARTS = 6

export default {
  name: 'AbroadTrendCharts',
  components: { ChartCard },
  props: {
    // [{ id, indicator, dimension, loading, data }]，数组顺序即展示顺序
    charts: {
      type: Array,
      default: () => []
    },
    // 指标下拉，不置灰 —— 多张图允许选同一个指标，只是维度不同
    metricOptions: {
      type: Array,
      default: () => []
    },
    dimensionOptions: {
      type: Array,
      default: () => []
    },
    compareLabels: {
      type: Array,
      default: null
    },
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    canAdd() {
      return this.charts.length < MAX_CHARTS
    },
    // 纵轴留多宽由所有图一起决定：每张图标签的宽窄不一样（预估收益是「$0.8」，
    // 出价只有「5」），各按自己的宽度收缩的话，并排两张图的绘图区左边线就对不齐，
    // 网格线、横轴第一格全跟着错开。取最宽的那个统一发下去，代价是标签短的那张图
    // 左边会空出一截
    sharedAxisWidth() {
      let max = 0
      this.charts.forEach(chart => {
        const width = yAxisWidth(chart.indicator, chart.data)
        if (width > max) max = width
      })
      return max
    }
  },
  watch: {
    // 张数变了槽位宽度也变了，得让每张图重新量一次尺寸
    'charts.length'() {
      this.$nextTick(this.resizeAll)
    },
    collapsed(val) {
      // 收起时容器是 display:none，量出来是 0，展开后要重新量
      if (!val) this.$nextTick(this.resizeAll)
    }
  },
  methods: {
    slotClass(index) {
      const count = this.charts.length
      if (count <= 1) return 'atc-slot--full'
      if (count === 2 || count === 4) return 'atc-slot--half'
      if (count === 5) return index < 2 ? 'atc-slot--half' : 'atc-slot--third'
      return 'atc-slot--third'
    },
    resizeAll() {
      const cards = this.$refs.cards || []
      cards.forEach(card => card.resize())
    }
  }
}
</script>

<style lang="scss" scoped>
.atc-head {
  display: flex;
  align-items: center;
  /* drawer_line 的 26px 只够放文字，这里要装按钮 */
  height: auto;
  min-height: 26px;
}
.atc-head__title {
  flex: 1;
  min-width: 0;
}
.atc-head__btn {
  flex: 0 0 auto;
  font-size: 14px;

  & + & {
    margin-left: 20px;
  }
}
.atc-grid {
  display: flex;
  flex-wrap: wrap;
  margin: 12px -8px 0;
}
.atc-slot {
  /* 槽位在 flex 行里本来就是等高的（align-items 默认 stretch），但卡片默认只有
     内容那么高 —— 图例行数不一样（比如一张拆了维度占两行、另一张合计只占一行）
     两张图就一高一低。这里让卡片撑满槽位，多出来的空白留在卡片底部 */
  display: flex;
  padding: 0 8px 16px;
  box-sizing: border-box;
}
/* 选的是 ChartCard 的根元素：Vue 2 会把父组件的 scope 属性也带到子组件根节点上，
   所以不用去引子组件里的类名 */
.atc-slot > * {
  flex: 1;
  min-width: 0;
}
.atc-slot--full {
  width: 100%;
}
.atc-slot--half {
  width: 50%;
}
.atc-slot--third {
  width: 33.3333%;
}

/* 窄屏统一降为单列（PRD 2.5.2.e） */
@media (max-width: 1200px) {
  .atc-slot--half,
  .atc-slot--third {
    width: 100%;
  }
}
</style>
